import { useState, useEffect, useCallback, useRef } from 'react'
import { Layout, Row, Col } from 'antd'
import { SettingOutlined, SaveFilled } from '@ant-design/icons'
import { useAppSelector, useAppDispatch } from 'hooks'
import { handleModalTriggerEvent, renderTranslation, stopPropagation } from 'Helpers/GenericHelpers'
import { TranslationKeys } from 'Constants/TranslationKeys'
import { deleteAnswers, getSettingsNames, sendAnswers } from 'Services/Settings/SettingsService'
import { removeEmptyValues, resetEdit, setSettingLoading, toggleChangeModal } from 'Slices/Settings/SettingsSlice'
import SettingsChangeModal from 'Components/Settings/SettingsChangeModal'
import QuestionsRows from './Components/QuestionsRows'
import AnswersRows from './Components/AnswersRows'
import { handleUpdateSettings } from 'Helpers/SettingsHelper'

const { Content } = Layout

function Settings() {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const editedAnswers = useAppSelector(state => state.present.settings.editedAnswers)
  const isLoading = useAppSelector(state => state.present.settings.isLoading)
  const deletedAnswers = useAppSelector(state => state.present.settings.deletedAnswers)
  const isSettingsVisible = useAppSelector(state => state.present.configuration.settingsVisibility)

  const settingsContainerRef = useRef(null)

  useEffect(() => {
    dispatch(getSettingsNames())
  }, [dispatch])

  const [settingsCollapse, setSettingsCollapse] = useState([])
  const [settingsSubCollapse, setSettingsSubCollapse] = useState([])

  const saveChanges = async () => {
    dispatch(setSettingLoading(true))
    const settingsToUpdate = new Set()
    try {
      if (Object.keys(deletedAnswers).length > 0) {
        const deletedSettingsNames = await dispatch(deleteAnswers()) // Wait for all deleteAnswers to complete
        deletedSettingsNames.forEach(settingsName => {
          settingsToUpdate.add(settingsName)
        })
      }
      if (Object.keys(editedAnswers).length > 0) {
        dispatch(removeEmptyValues())
        if (Object.keys(editedAnswers).length > 0) {
          const sentSettingsNames = await dispatch(sendAnswers()) // Wait for sendAnswers to complete
          sentSettingsNames.forEach(settingsName => {
            settingsToUpdate.add(settingsName)
          })
        }
      }
    } finally {
      handleUpdateSettings(dispatch, settingsToUpdate)
    }
  }

  const handleClickOutside = useCallback(
    event => {
      const callback = () => {
        event.preventDefault()
        stopPropagation(event)
        dispatch(toggleChangeModal(true))
      }
      const hasChanges = Object.keys(editedAnswers).length > 0

      handleModalTriggerEvent(event, settingsContainerRef?.current, hasChanges, callback, null, null)
    },
    [dispatch, editedAnswers],
  )

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [handleClickOutside])

  useEffect(() => {
    settingsCollapse.forEach(settingsName => {
      const questionHeader = document.getElementById(`question-${settingsName}`)
      const answerHeader = document.getElementById(`answer-${settingsName}`)
      const answerContainer = document.getElementById('answer-container')
      const questionCollapse = document.getElementById(`question-collapse-${settingsName}`)

      const collapseMargin = answerHeader.scrollWidth > answerHeader.clientWidth

      questionCollapse.style.marginBottom = collapseMargin ? '17px' : '0px'

      setTimeout(() => {
        const questionPosition = questionHeader.getBoundingClientRect()?.top
        const answerContainerPosition = answerContainer.getBoundingClientRect()?.top
        const position = questionPosition - answerContainerPosition

        answerHeader.style.top = `${position + 0}px`
      }, 0)
    })
  }, [settingsCollapse, settingsSubCollapse, isLoading])

  return isSettingsVisible ? (
    <Content className='page-content settings pa-5' id='page-content-settings' ref={settingsContainerRef}>
      <Row className='' wrap={true}>
        <SettingsChangeModal
          onChangeConfirm={saveChanges}
          onChangeCancel={() => {
            dispatch(resetEdit())
          }}
          bodyref={settingsContainerRef}
        />
        <Col xxl={5} xl={7} lg={9} md={12} sm={14}>
          <SettingOutlined className='generic-icon mr-5' />
          <div className='d-inline-block'>{renderTranslation(language, TranslationKeys.settingsTitle)}</div>
        </Col>
        <Col xxl={19} xl={17} lg={15} md={12} sm={10}>
          <div className='title-box'>
            <SaveFilled
              className={
                'generic-icon save-icon channel-save-icon ant-col' +
                (Object.keys(editedAnswers).length > 0 ? ' active' : '')
              }
              onClick={Object.keys(editedAnswers).length > 0 ? saveChanges : null}
            />
          </div>
        </Col>
        <Col xxl={5} xl={7} lg={9} md={12} sm={14}>
          <QuestionsRows
            collapse={settingsCollapse}
            subCollapse={settingsSubCollapse}
            setCollapse={setSettingsCollapse}
            setSubCollapse={setSettingsSubCollapse}
          />
        </Col>
        {/*ANSWER COLUMNS*/}
        <Col
          xxl={19}
          xl={17}
          lg={15}
          md={12}
          sm={10}
          id={'answer-container'}
          className='overflow-y-hidden overflow-x-hidden'
        >
          <AnswersRows collapse={settingsCollapse} subCollapse={settingsSubCollapse} />
        </Col>
      </Row>
    </Content>
  ) : null
}

export default Settings
